import type { EntityATI } from '@atlassian/search-client/common/types';

import { universalTypeFilterId } from '../../../common/constants/filters';
import { type TypeFilterValueKey } from '../../../common/constants/filters/universal-type';
import { type ProductKeys } from '../../../common/constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import type { SearchStateFilters } from '../../../common/constants/search';

import { getUniversalTypeEntities } from './get-universal-type-entities';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getUniversalTypeEntities } from './get-universal-type-entities';

/** Return type for type entity lookups — maps entity keys to display labels. */
export type UseTypeEntitiesResult = {
	selectedEntities: string[];
	entitiesForQuery: import('@atlassian/search-client').EntityATI[];
	entitiesForResultsCountQuery: import('@atlassian/search-client').EntityATI[];
	selectedTypes: TypeFilterValueKey[];
};

/** The search state fields needed to compute type entities. */
export type TypeEntitiesInput = {
	availableProducts: ProductKeys[];
	filters: SearchStateFilters;
	selectedProducts: ProductKeys[];
	allFilterExcludedProducts: ProductKeys[];
};

/** Configuration for type entity resolution (third-party integrations). */
export type TypeEntitiesConfig = {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
};

/**
 * Returns the set of content-type entity identifiers (e.g. pages, issues, whiteboards)
 * that should be included in a search query, based on the currently selected products
 * and available filter configuration.
 *
 * Pure function — safe for SSR and non-React contexts.
 */
export const getTypeEntities = (
	{ availableProducts, filters, selectedProducts, allFilterExcludedProducts }: TypeEntitiesInput,
	{ thirdPartyConfigs }: TypeEntitiesConfig,
): {
	selectedEntities: TypeFilterValueKey[];
	entitiesForQuery: EntityATI[];
	entitiesForResultsCountQuery: EntityATI[];
	selectedTypes: TypeFilterValueKey[];
} => {
	// Filter out excluded products from "All" filter
	const allFilterIncludedProducts = availableProducts.filter(
		(product) => !allFilterExcludedProducts.includes(product),
	);
	const productsToSearch = selectedProducts[0] ? [selectedProducts[0]] : allFilterIncludedProducts;
	const typeFilter = filters[universalTypeFilterId];

	return getUniversalTypeEntities(
		productsToSearch,
		typeFilter,
		thirdPartyConfigs,
		availableProducts,
		selectedProducts,
	);
};
